/** Mirrors the enriched WorkflowVersionSummary + import shapes in
 * backend/app/schemas/workflows_api.py (design spec §5–§7). */
import type { WorkflowOverlayJson } from '@/types/workflowConfig'

export interface DeleteBlocker {
  code: 'current' | 'pinned_instances' | 'latest_base' | 'tagged' | string
  message: string
}

export interface VersionProvenance {
  imported_from?: {
    source_label?: string | null
    workflow_id?: string
    version_number?: number | null
    exported_at?: string | null
    base_fingerprint?: string | null
  }
  promoted?: PromotedProvenance
}

export const CANDIDATE_TAG = 'candidate'

export interface PromotedProvenance {
  run_url: string | null
  run_id: string | null
  verdict: string
  notes: string | null
  promoted_by: string
  promoted_at: string
  previous_version_id: string | null
  refreshed_from: number | null
}

export interface PromoteEvidence {
  run_url?: string
  run_id?: string
  verdict: 'pass'
  notes?: string
}

export interface WorkflowVersionRow {
  id: string
  version_number: number
  published_at: string | null
  created_by: string | null
  created_at: string
  base_version_id: string | null
  tags: string[]
  archived_at: string | null
  is_current: boolean
  origin: 'seeded' | 'published' | 'imported'
  created_by_name: string | null
  delete_blockers: DeleteBlocker[]
  provenance: VersionProvenance | null
}

/** Full projection of a WorkflowVersion row including `definition` — mirrors
 * backend `WorkflowVersionDetail(WorkflowVersionSummary)`. Returned by
 * publish/pack-apply/promote/version-detail endpoints. */
export interface WorkflowVersionDetail extends WorkflowVersionRow {
  definition: unknown
  required_capabilities: string[]
  triggers: Record<string, unknown>[]
  overlay_snapshot: Record<string, unknown> | null
}

export interface VersionImportEnvelope {
  format: 'workflow-version-export'
  format_version: 1
  exported_at?: string | null
  source_label?: string | null
  workflow: { id: string; source?: string; name?: string }
  version?: { version_number?: number; tags?: string[] }
  base?: { fingerprint?: string | null; base_version_number?: number | null }
  overlay: WorkflowOverlayJson | null
}

export interface ImportPreview {
  valid: boolean
  findings: { path: string; message: string; code: string }[]
  base_match: 'match' | 'mismatch' | 'unknown'
  diff: { added: string[]; overridden: string[]; disabled: string[]; settings: string[] }
  no_op: boolean
  draft_differs: boolean
  source_label: string | null
  source_version_number: number | null
}

export interface ImportApplyResult {
  no_op: boolean
  version: WorkflowVersionDetail | null
  diff: ImportPreview['diff']
}
